import { FormEvent, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  validateToken,
  getUploadUrl,
  submitApplication,
  type UploadField,
  type PresignedUpload,
} from '../api/publicApplication';

const STATES = [
  'AL','AK','AZ','AR','CA','CO','CT','DE','DC','FL','GA','HI','ID','IL','IN','IA',
  'KS','KY','LA','ME','MD','MA','MI','MN','MS','MO','MT','NE','NV','NH','NJ','NM',
  'NY','NC','ND','OH','OK','OR','PA','RI','SC','SD','TN','TX','UT','VT','VA','WA',
  'WV','WI','WY',
];

const COMP_TIERS = [65,70,75,80,85,90,95,100,105,110,115,120,125,130,135,140];

const UPLOAD_LABELS: Record<UploadField, string> = {
  resume: 'Resume',
  license: 'Current License Certificate',
  voidedCheck: 'Voided Check (optional)',
};

const UPLOAD_ACCEPT: Record<UploadField, string> = {
  resume: 'application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  license: 'application/pdf,image/jpeg,image/png',
  voidedCheck: 'application/pdf,image/jpeg,image/png',
};

const inputCls =
  'w-full bg-dg-panel border border-dg-border rounded-md px-3 py-3 text-sm text-white placeholder-dg-mist/60 focus:outline-none focus:border-dg-red';

function Field({ label, required, children, hint }: { label: string; required?: boolean; children: React.ReactNode; hint?: string }) {
  return (
    <label className="block">
      <div className="text-xs uppercase tracking-wider text-dg-mist mb-1.5">
        {label}
        {required ? <span className="text-dg-red ml-1">*</span> : null}
      </div>
      {children}
      {hint ? <div className="text-[11px] text-dg-mist/70 mt-1">{hint}</div> : null}
    </label>
  );
}

type PageState = 'loading' | 'invalid' | 'ready' | 'submitting' | 'submitted';
type UploadState = { field: UploadField; key: string; mimeType: string; size: number; filename: string };

export default function PublicApply() {
  const { token } = useParams<{ token: string }>();
  const [state, setState] = useState<PageState>('loading');
  const [tenantName, setTenantName] = useState<string>('');
  const [error, setError] = useState<string | null>(null);
  const [uploads, setUploads] = useState<UploadState[]>([]);
  const [uploadingField, setUploadingField] = useState<UploadField | null>(null);

  // Form fields
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [dob, setDob] = useState('');
  const [addressLine1, setAddressLine1] = useState('');
  const [city, setCity] = useState('');
  const [stateCode, setStateCode] = useState('');
  const [zip, setZip] = useState('');
  const [hasExistingLicense, setHasExistingLicense] = useState<'' | 'yes' | 'no'>('');
  const [licenseNumber, setLicenseNumber] = useState('');
  const [licenseState, setLicenseState] = useState('');
  const [compTier, setCompTier] = useState<number | ''>('');
  const [website, setWebsite] = useState(''); // honeypot

  useEffect(() => {
    if (!token) return;
    (async () => {
      try {
        const info = await validateToken(token);
        setTenantName(info.tenant?.name || 'The Disruption Group');
        setState('ready');
      } catch (_e) {
        setState('invalid');
      }
    })();
  }, [token]);

  async function onUpload(field: UploadField, file: File) {
    if (!token) return;
    setUploadingField(field);
    setError(null);
    try {
      const presign: PresignedUpload = await getUploadUrl(token, {
        field,
        filename: file.name,
        mimeType: file.type || 'application/octet-stream',
        size: file.size,
      });
      // In production, presign.url is an https://... R2 URL we PUT directly.
      // In dev/test the URL is a sentinel "local://..."; the route layer
      // stores only the key, so we don't need to actually upload anywhere.
      if (presign.url.startsWith('http')) {
        await fetch(presign.url, {
          method: presign.method,
          headers: presign.headers,
          body: file,
        });
      }
      setUploads((prev) => [
        ...prev.filter((u) => u.field !== field),
        { field, key: presign.key, mimeType: file.type, size: file.size, filename: file.name },
      ]);
    } catch (e: unknown) {
      const err = e as { response?: { data?: { error?: { message?: string } } } };
      setError(err.response?.data?.error?.message || 'Upload failed');
    } finally {
      setUploadingField(null);
    }
  }

  function clientValidate(): string | null {
    if (!firstName.trim() || !lastName.trim()) return 'First and last name are required.';
    if (!email.trim()) return 'Email is required.';
    if (!phone.trim()) return 'Phone is required.';
    if (!dob) return 'Date of birth is required.';
    if (!addressLine1.trim() || !city.trim() || !stateCode || !zip.trim()) return 'Full address is required.';
    if (!/^\d{5}$/.test(zip)) return 'Zip must be 5 digits.';
    if (hasExistingLicense === '') return 'Please answer the existing license question.';
    if (hasExistingLicense === 'yes' && (!licenseNumber.trim() || !licenseState)) return 'License number and state are required.';
    if (compTier === '') return 'Please select a comp tier.';
    return null;
  }

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (!token) return;
    setError(null);
    const v = clientValidate();
    if (v) {
      setError(v);
      return;
    }
    setState('submitting');
    try {
      await submitApplication(token, {
        firstName,
        lastName,
        email,
        phone,
        dob,
        addressLine1,
        city,
        state: stateCode,
        zip,
        hasExistingLicense: hasExistingLicense === 'yes',
        licenseNumber: hasExistingLicense === 'yes' ? licenseNumber : '',
        licenseState: hasExistingLicense === 'yes' ? licenseState : '',
        compTier: Number(compTier),
        website,
        uploads: uploads.map((u) => ({
          field: u.field,
          s3Key: u.key,
          mimeType: u.mimeType,
          size: u.size,
        })),
      });
      setState('submitted');
    } catch (e: unknown) {
      const err = e as { response?: { data?: { error?: { message?: string; code?: string } } } };
      const msg = err.response?.data?.error?.message || 'Submission failed.';
      setError(msg);
      setState('ready');
    }
  }

  if (state === 'loading') {
    return (
      <div className="min-h-screen bg-dg-ink flex items-center justify-center text-white">
        <div className="text-dg-mist">Loading…</div>
      </div>
    );
  }

  if (state === 'invalid') {
    return (
      <div className="min-h-screen bg-dg-ink flex items-center justify-center text-white px-6">
        <div className="max-w-md text-center">
          <h1 className="h-display text-3xl text-dg-red mb-3">Link Invalid</h1>
          <p className="text-dg-mist">
            This application link is no longer valid. It may have expired, been
            revoked, or already used. Please contact your recruiter for a new
            link.
          </p>
        </div>
      </div>
    );
  }

  if (state === 'submitted') {
    return (
      <div className="min-h-screen bg-dg-ink flex items-center justify-center text-white px-6">
        <div className="max-w-md text-center">
          <h1 className="h-display text-3xl mb-3" style={{ color: 'var(--dg-red)' }}>Thanks, {firstName || 'we got it'}!</h1>
          <p className="text-dg-mist mb-2">
            Your application has been received. A recruiter from {tenantName} will reach
            out within 24 hours.
          </p>
        </div>
      </div>
    );
  }

  const submitting = state === 'submitting';

  return (
    <div className="min-h-screen bg-dg-ink text-white">
      <div className="max-w-2xl mx-auto px-4 sm:px-6 py-8">
        <div className="mb-8">
          <h1 className="h-display text-3xl sm:text-4xl mb-2" style={{ color: 'var(--dg-red)' }}>
            Apply to {tenantName}
          </h1>
          <p className="text-dg-mist text-sm">
            Fill out the form below. All required fields are marked with <span className="text-dg-red">*</span>.
          </p>
        </div>

        {error ? (
          <div className="mb-4 px-4 py-3 rounded border border-dg-red/40 bg-dg-red/10 text-sm">
            {error}
          </div>
        ) : null}

        <form onSubmit={onSubmit} className="space-y-5 bg-dg-panel/60 border border-dg-border rounded-lg p-5 sm:p-6">
          {/* Honeypot — visually hidden */}
          <div aria-hidden="true" style={{ position: 'absolute', left: '-9999px', height: 0, overflow: 'hidden' }}>
            <label>
              Website
              <input
                type="text"
                tabIndex={-1}
                autoComplete="off"
                value={website}
                onChange={(e) => setWebsite(e.target.value)}
              />
            </label>
          </div>

          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="First Name" required>
              <input className={inputCls} value={firstName} onChange={(e) => setFirstName(e.target.value)} required />
            </Field>
            <Field label="Last Name" required>
              <input className={inputCls} value={lastName} onChange={(e) => setLastName(e.target.value)} required />
            </Field>
          </div>

          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Email" required>
              <input className={inputCls} type="email" value={email} onChange={(e) => setEmail(e.target.value)} required />
            </Field>
            <Field label="Phone" required>
              <input className={inputCls} type="tel" placeholder="(555) 555-5555" value={phone} onChange={(e) => setPhone(e.target.value)} required />
            </Field>
          </div>

          <Field label="Date of Birth" required hint="You must be 18 or older.">
            <input className={inputCls} type="date" value={dob} onChange={(e) => setDob(e.target.value)} required />
          </Field>

          <Field label="Address" required>
            <input className={inputCls} value={addressLine1} onChange={(e) => setAddressLine1(e.target.value)} required />
          </Field>

          <div className="grid sm:grid-cols-3 gap-4">
            <Field label="City" required>
              <input className={inputCls} value={city} onChange={(e) => setCity(e.target.value)} required />
            </Field>
            <Field label="State" required>
              <select className={inputCls} value={stateCode} onChange={(e) => setStateCode(e.target.value)} required>
                <option value="">Select…</option>
                {STATES.map((s) => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </select>
            </Field>
            <Field label="Zip" required>
              <input className={inputCls} value={zip} onChange={(e) => setZip(e.target.value)} maxLength={5} pattern="\d{5}" required />
            </Field>
          </div>

          <Field label="Do you have an existing insurance license?" required>
            <select className={inputCls} value={hasExistingLicense} onChange={(e) => setHasExistingLicense(e.target.value as 'yes' | 'no')} required>
              <option value="">Select…</option>
              <option value="yes">Yes</option>
              <option value="no">No</option>
            </select>
          </Field>

          {hasExistingLicense === 'yes' ? (
            <div className="grid sm:grid-cols-2 gap-4">
              <Field label="License Number" required>
                <input className={inputCls} value={licenseNumber} onChange={(e) => setLicenseNumber(e.target.value)} required />
              </Field>
              <Field label="License State" required>
                <select className={inputCls} value={licenseState} onChange={(e) => setLicenseState(e.target.value)} required>
                  <option value="">Select…</option>
                  {STATES.map((s) => (
                    <option key={s} value={s}>{s}</option>
                  ))}
                </select>
              </Field>
            </div>
          ) : null}

          <Field label="Comp Tier Preference" required>
            <select className={inputCls} value={compTier} onChange={(e) => setCompTier(e.target.value === '' ? '' : Number(e.target.value))} required>
              <option value="">Select…</option>
              {COMP_TIERS.map((t) => (
                <option key={t} value={t}>{t}%</option>
              ))}
            </select>
          </Field>

          <div className="space-y-3 border-t border-dg-border pt-5">
            <div className="text-xs uppercase tracking-wider text-dg-mist">Documents</div>
            {(['resume', 'license', 'voidedCheck'] as UploadField[]).map((field) => {
              const u = uploads.find((x) => x.field === field);
              return (
                <div key={field} className="flex items-center justify-between gap-3 text-sm">
                  <div className="flex-1">
                    <div className="font-medium">{UPLOAD_LABELS[field]}</div>
                    {u ? <div className="text-xs text-dg-mist">{u.filename}</div> : null}
                  </div>
                  <label className="cursor-pointer text-dg-red hover:text-dg-red-hover">
                    <input
                      type="file"
                      className="hidden"
                      accept={UPLOAD_ACCEPT[field]}
                      onChange={(e) => {
                        const f = e.target.files?.[0];
                        if (f) onUpload(field, f);
                      }}
                    />
                    {uploadingField === field ? 'Uploading…' : u ? 'Replace' : 'Upload'}
                  </label>
                </div>
              );
            })}
          </div>

          <div className="text-xs text-dg-mist/80">
            By submitting, you agree to receive text messages from {tenantName} regarding your application.
          </div>

          <button
            type="submit"
            disabled={submitting}
            className="w-full py-3 rounded-md bg-dg-red hover:bg-dg-red-hover disabled:opacity-60 transition-colors font-medium"
          >
            {submitting ? 'Submitting…' : 'Submit Application'}
          </button>
        </form>
      </div>
    </div>
  );
}
